import { X } from "lucide-react";
import { createPortal } from "react-dom";

interface Notification {
  id: string;
  title: string;
  body: string;
  time: string;
  icon: React.ReactNode;
}

interface NotificationDrawerProps {
  open: boolean;
  onClose: () => void;
  notifications?: Notification[];
}

// Default static notifications — replace with API data when endpoint is available
const DEFAULT_NOTIFICATIONS: Notification[] = [
  {
    id: "1",
    title: "Submission deadline approaching",
    body: "AI Innovation Challenge closes soon. Submit your idea before the deadline.",
    time: "2 hours ago",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22Z" fill="#FF6B6B" />
        <path d="M18 16V11C18 7.93 16.36 5.36 13.5 4.68V4C13.5 3.17 12.83 2.5 12 2.5C11.17 2.5 10.5 3.17 10.5 4V4.68C7.63 5.36 6 7.92 6 11V16L4 18V19H20V18L18 16Z" fill="#FF6B6B" />
      </svg>
    ),
  },
  {
    id: "2",
    title: "Mentor feedback received",
    body: "Your mentor reviewed your idea and left comments.",
    time: "5 hours ago",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M4 4H20V16H6L4 18V4Z" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: "3",
    title: "Team updated",
    body: "A new member joined your team.",
    time: "1 day ago",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="7" r="4" stroke="#00A07E" strokeWidth="2" />
        <path d="M4 20C4 16 7 14 12 14C17 14 20 16 20 20" stroke="#00A07E" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: "4",
    title: "Idea status changed",
    body: "Your idea has been moved to the Review stage.",
    time: "3 days ago",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M12 3L4 9V21H10V15H14V21H20V9L12 3Z" stroke="#4A90E2" strokeWidth="2" />
      </svg>
    ),
  },
];

export default function NotificationDrawer({
  open,
  onClose,
  notifications = DEFAULT_NOTIFICATIONS,
}: NotificationDrawerProps) {
  return createPortal(
    <>
      {/* BACKDROP */}
      <div
        className={`fixed inset-0 bg-black/40 transition-opacity duration-300 z-[999999]
          ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={onClose}
      />

      {/* DRAWER */}
      <div
        className={`fixed top-0 right-0 w-full sm:w-[420px] h-full overflow-auto bg-white
          shadow-xl z-[1000000] transition-transform duration-300
          ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        {/* HEADER */}
        <div className="flex items-center justify-between px-6 py-5 border-b">
          <h2 className="text-lg font-semibold text-[#1A1F36] flex items-center gap-2">
            <span className="text-[#FF6B6B]">🔔</span> All Notifications
          </h2>
          <button onClick={onClose}>
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* CONTENT */}
        <div className="px-6 py-6 h-full space-y-6">
          {/* ✅ Empty state */}
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-gray-400">
              <span className="text-4xl mb-3">🔕</span>
              <p className="text-sm">No notifications yet</p>
            </div>
          ) : (
            notifications.map((n, i) => (
              <div
                key={n.id}
                className={`flex gap-4 pb-4 ${i < notifications.length - 1 ? "border-b border-gray-200/60" : ""}`}
              >
                <div className="p-3 rounded-[16px]">{n.icon}</div>
                <div>
                  <p className="text-sm font-semibold text-[#1A1F36]">{n.title}</p>
                  <p className="text-sm text-[#6E7891]">{n.body}</p>
                  <p className="text-xs text-gray-400 mt-1">{n.time}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>,
    document.body
  );
}
